import { ConversationCloseIconButton } from "@circle-react-shared/AiConversation/header/ConversationCloseIconButton";
import { ConversationFullscreenToggleButton } from "@circle-react-shared/AiConversation/header/ConversationFullscreenToggleButton";
import { ConversationHeaderBar } from "@circle-react-shared/AiConversation/header/ConversationHeaderBar";
import { CopyLinkIconButton } from "@circle-react-shared/CopyLinkIconButton";
import { Avatar } from "@circleco/compass/components/Avatar";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { Typography } from "@circleco/compass/components/Typography";
import { useShallow } from "zustand/react/shallow";

import { t } from "@/i18n-js/instance";

import { useAvatarConversationPanelStore } from "../useAvatarConversationPanelStore";
import { buildAvatarPanelEntryUrl } from "./urlParams";

interface HeaderProps {
  avatarId?: number;
  avatarUrl?: string;
  name?: string;
  title?: string | null;
}

export const Header = ({ avatarId, avatarUrl, name, title }: HeaderProps) => {
  const {
    activeChatId,
    isFullscreen,
    closePanel,
    showAvatars,
    startNewConversation,
    toggleFullscreen,
  } = useAvatarConversationPanelStore(
    useShallow(s => ({
      activeChatId: s.activeChatId,
      isFullscreen: s.panelState === "fullscreen",
      closePanel: s.closePanel,
      showAvatars: s.showAvatars,
      startNewConversation: s.startNewConversation,
      toggleFullscreen: s.toggleFullscreen,
    })),
  );
  const newConversationLabel = t("avatars.conversation_panel.new_conversation");
  const backLabel = t("avatars.conversation_panel.back_to_avatars");

  return (
    <ConversationHeaderBar>
      <div className="flex min-w-0 items-center gap-2">
        <Tooltip content={backLabel} side="bottom">
          <IconButton
            icon="arrow-left"
            size="sm"
            variant="ghost"
            aria-label={backLabel}
            onClick={showAvatars}
          />
        </Tooltip>
        {name && (
          <>
            <Avatar name={name} src={avatarUrl} size="sm" decorative />
            <Typography variant="heading-sm" truncate>
              {title ?? name}
            </Typography>
          </>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Tooltip content={newConversationLabel} side="bottom">
          <IconButton
            icon="edit"
            size="sm"
            variant="ghost"
            aria-label={newConversationLabel}
            disabled={avatarId === undefined}
            onClick={() =>
              avatarId !== undefined && startNewConversation(avatarId)
            }
          />
        </Tooltip>
        <CopyLinkIconButton
          getUrl={() =>
            buildAvatarPanelEntryUrl(activeChatId, isFullscreen, avatarId)
          }
        />
        <ConversationFullscreenToggleButton
          data-testid="avatar-conversation-panel-fullscreen"
          isFullscreen={isFullscreen}
          onToggle={toggleFullscreen}
        />
        <ConversationCloseIconButton
          data-testid="avatar-conversation-panel-close"
          icon="cross"
          label={t("circle_ai_shell.close_panel")}
          onClose={closePanel}
        />
      </div>
    </ConversationHeaderBar>
  );
};
